// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

/**
 * Setting Editor Modal
 * 
 * RESPONSIBILITY: Add/Edit setting options
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  Typography
} from '@mui/material';

const SettingEditor = ({ isOpen, setting, option, onSave, onCancel }) => {
  const isGlossary = setting?.category === 'translation_glossary';
  const [formData, setFormData] = useState({
    value: '',
    label: '',
    description: '',
    display_order: 0
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (option) {
      // Editing existing option
      setFormData({
        value: option.value || '',
        label: option.label || '',
        description: option.description || '',
        display_order: option.display_order || 0
      });
    } else {
      // Adding new option - find max display_order
      const maxOrder = setting?.setting_value
        ? Math.max(...setting.setting_value.map(o => o.display_order || 0), 0)
        : 0;
      
      setFormData({
        value: '',
        label: '',
        description: '',
        display_order: maxOrder + 1
      });
    }
  }, [option, setting]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'display_order' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      // Validate
      if (!formData.value || !formData.label) {
        throw new Error(isGlossary ? 'Source and Target are required' : 'Value and Label are required');
      }

      // Check for duplicate value (when adding new)
      if (!option && setting?.setting_value) {
        const exists = setting.setting_value.some(o => o.value === formData.value);
        if (exists) {
          throw new Error(isGlossary ? 'This source term already exists' : 'Value already exists');
        }
      }

      await onSave(formData);
    } catch (err) {
      setError(err.message || 'Failed to save option');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        {option ? 'Edit Option' : 'Add New Option'} - {setting?.display_name}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          <TextField
            fullWidth
            label={isGlossary ? 'Source (original term)' : 'Value (Code)'}
            name="value"
            value={formData.value}
            onChange={handleChange}
            placeholder={isGlossary ? 'e.g. 佛罗伦萨小镇' : 'booking'}
            required
            disabled={!!option && !isGlossary}
            helperText={isGlossary ? 'Original term in source language' : 'Unique identifier (lowercase, no spaces, use hyphens)'}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label={isGlossary ? 'Target (translation)' : 'Label (Display Name)'}
            name="label"
            value={formData.label}
            onChange={handleChange}
            placeholder={isGlossary ? 'e.g. Florentia Village' : 'Booking/Reservation'}
            required
            helperText={isGlossary ? 'Exact translation to use in English' : 'What users will see in dropdown'}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            multiline
            rows={2}
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder={isGlossary ? 'Optional note' : 'Room booking, reservation changes, cancellations'}
            helperText={isGlossary ? 'Optional note for this term' : 'Brief explanation of this option'}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="number"
            label="Display Order"
            name="display_order"
            value={formData.display_order}
            onChange={handleChange}
            inputProps={{ min: 0 }}
            helperText="Order in list (lower numbers appear first)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SettingEditor;

